import React, { useCallback, useMemo } from 'react';
import ItemContent, { IOnChangeInputs } from '../../../components/ItemContent/ItemContent';
import { IEditForm } from '../../../types/interfaces/IEditForm';
import { NSPackages } from '../../../types/interfaces/NSPackages';
import { EStatus } from '../../../types/enums/EStatus';
import { useTranslation } from 'react-i18next';
import tPaths from '../../../utils/tPaths';
import { endpoints } from '../../../utils/endpoints';
import { EPaths } from '../../../types/enums/EPaths';
import usePermissionsStatus from '../../../utils/usePermissionsStatus';

const AddSystemUser = () => {
  const { t } = useTranslation();
  const { arrayToPermissions, permissionsToArray } = usePermissionsStatus();

  const dataMapper = useCallback((): IEditForm[][] => {
    return [
      [
        {
          type: 'input',
          fieldName: 'nameSurname',
          label: t(tPaths.editFormLabels.name),
        },
        {
          type: 'input',
          fieldName: 'email',
          label: t(tPaths.editFormLabels.email),
        },
        {
          type: 'createPassword',
          fieldName: 'password',
          label: t(tPaths.editFormLabels.password),
        },
      ],
      [
        {
          type: 'checkboxGrid',
          fieldName: 'permissions',
          label: t(tPaths.editFormLabels.email),
          initialValue: arrayToPermissions([]),
          checkboxGrid: [
            { key: 'view', label: t(tPaths.systemUsers.view) },
            { key: 'edit', label: t(tPaths.systemUsers.edit) },
          ],
          items: [
            {
              fieldName: 'packages',
              label: t(tPaths.sideMenu.packages),
            },
            {
              fieldName: 'customers',
              label: t(tPaths.sideMenu.customers),
            },
            {
              fieldName: 'systemUsers',
              label: t(tPaths.sideMenu.systemUsers),
            },
            {
              fieldName: 'carrierStatus',
              label: t(tPaths.sideMenu.carrierStatus),
            },
            {
              fieldName: 'notifications',
              label: t(tPaths.sideMenu.notifications),
            },
            {
              fieldName: 'appSettings',
              label: t(tPaths.sideMenu.settings),
            },
            {
              fieldName: 'promocodes',
              label: t(tPaths.sideMenu.promoCodes),
            },
            {
              fieldName: 'promoCampaigns',
              label: t(tPaths.sideMenu.promoCampaigns),
            },
          ],
        },
      ],
    ];
  }, [arrayToPermissions, t]);

  const updateDataFormatter = useCallback(
    (fields: any) => ({
      nameSurname: fields.nameSurname.value,
      email: fields.email.value,
      status: fields.status ? EStatus.Active : EStatus.Inactive,
      permissions: permissionsToArray(fields.permissions.value),
      password: fields.password.value,
    }),
    [permissionsToArray],
  );

  const switches = useMemo(() => [{ key: 'status', label: t(tPaths.itemContent.active) }], [t]);
  const switchesDataMapper = useCallback((data: NSPackages.IPackageDetails) => {
    return { status: data.status.current === EStatus.Active };
  }, []);

  const handleOnChange = useCallback(({ fieldName, value }: IOnChangeInputs) => {
    if (fieldName === 'permissions') {
      return {
        carrierStatus: {
          view: value.carrierStatus.view,
          edit: value.carrierStatus.edit && value.carrierStatus.view,
        },
        customers: {
          view: value.customers.view,
          edit: value.customers.edit && value.customers.view,
        },
        notifications: {
          view: value.notifications.view,
          edit: value.notifications.edit && value.notifications.view,
        },
        packages: {
          view: value.packages.view,
          edit: value.packages.edit && value.packages.view,
        },
        systemUsers: {
          view: value.systemUsers.view,
          edit: value.systemUsers.edit && value.systemUsers.view,
        },
        appSettings: {
          view: value.appSettings.view,
          edit: value.appSettings.edit && value.appSettings.view,
        },
        promocodes: {
          view: value.promocodes.view,
          edit: value.promocodes.edit && value.promocodes.view,
        },
        promoCampaigns: {
          view: value.promoCampaigns.view,
          edit: value.promoCampaigns.edit && value.promoCampaigns.view,
        },
      };
    } else {
      return value;
    }
  }, []);

  return (
    <ItemContent
      title={t(tPaths.itemContent.addSystemUser)}
      path={EPaths.SystemUsers}
      contentMapper={dataMapper}
      updateDataFormatter={updateDataFormatter}
      switches={switches}
      switchesDataMapper={switchesDataMapper}
      addItem={true}
      apiUrl={endpoints.systemUsers}
      onChangeFormatter={handleOnChange}
    />
  );
};

export default AddSystemUser;
